import React, { useMemo } from 'react';

import { TextInput } from '@components/admin/Forms';
import { Button as CommonButton, ButtonProps } from '@components/Button';
import { useSearch } from '@hooks/useSearch';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createValidate } from '@utils/forms';
import { createStrings } from '@utils/local';
import { Form, Formik, useFormikContext } from 'formik';
import * as t from 'io-ts';
import * as h from 'tyrann-io';

export interface FilterProps {}

interface FieldProps {
  name: 'd2d_no' | 'fc_manager' | 'vessel' | 'author';
}

const strings = createStrings({
  ja: {
    d2d_no: 'D2D番号',
    d2dNoPlaceholder: 'Yから始まる番号',
    fc_manager: '作成者',
    vessel: '船名',
    author: '申込者',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexInline,
    alignItems: 'flex-start',
    alignContent: 'center',
    columnGap: theme.spacing(3),
    rowGap: '0.5rem',
    flexWrap: 'wrap',
  },
  field: {
    ...common.normalText,
    ...common.flexInline,
    alignItems: 'stretch',
    [theme.breakpoints.down('sm')]: {
      height: 'auto',
    },
  },
  label: {
    ...common.flexInline,
    alignItems: 'flex-start',
    width: 'initial',
    minWidth: 'initial',
    paddingTop: 5,
  },
  input: {
    ...common.middleText,
    padding: '6px 10px',
    width: '100%',
    borderRadius: 2,
    borderColor: theme.palette.grey[500],
    borderStyle: 'solid',
    borderWidth: 1,
  },
  button: {
    flex: 'none',
  },
}));

const searchType = t.type({
  d2d_no: h.omittable(h.string()),
  fc_manager: h.omittable(h.string()),
  vessel: h.omittable(h.string()),
  author: h.omittable(h.string()),
  page: h.omittable(h.number()),
});

const textInputPropsPool: {
  [key in FieldProps['name']]: {
    name: FieldProps['name'];
    label: string;
    placeholder?: string;
    prefix?: string;
  };
} = {
  d2d_no: {
    name: 'd2d_no',
    label: strings.d2d_no,
    prefix: 'Y-',
  },
  fc_manager: {
    name: 'fc_manager',
    label: strings.fc_manager,
  },
  author: {
    name: 'author',
    label: strings.author,
  },
  vessel: {
    name: 'vessel',
    label: strings.vessel,
  },
};

const Field: React.FC<FieldProps> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { name } = props;
  const textInputProps = useMemo(() => {
    return textInputPropsPool[name];
  }, [name]);

  return (
    <div className={classes.field}>
      <TextInput
        {...textInputProps}
        formClasses={{
          label: classes.label,
        }}
      />
    </div>
  );
};

const Button: React.FC<Omit<ButtonProps, 'color' | 'size'>> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { type } = props;

  if (type === 'reset') {
    const { resetForm, handleSubmit } = useFormikContext();

    return (
      <div className={classes.button}>
        <CommonButton
          {...props}
          color="primary"
          size="small"
          onClick={() => {
            resetForm({
              values: {
                d2d_no: undefined,
                fc_manager: undefined,
                vessel: undefined,
                page: 0,
              },
            });
            handleSubmit();
          }}
        />
      </div>
    );
  }

  return (
    <div className={classes.button}>
      <CommonButton {...props} color="primary" size="small" />
    </div>
  );
};

interface Aggregation {
  Field: typeof Field;
  Button: typeof Button;
}

export const Filter: React.FC<React.PropsWithChildren<FilterProps>> & Aggregation = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { children } = props;
  const { search, updateSearch } = useSearch(searchType);
  const initialValues: t.TypeOf<typeof searchType> = {
    d2d_no: search.d2d_no,
    fc_manager: search.fc_manager,
    author: search.author,
    vessel: search.vessel,
    page: search.page ?? 1,
  };

  return (
    <Formik
      initialValues={initialValues}
      validate={createValidate(searchType)}
      enableReinitialize
      onSubmit={(values) => {
        updateSearch({
          ...values,
          page: 1,
        });
      }}
    >
      <Form className={classes.container}>{children}</Form>
    </Formik>
  );
};

Filter.Field = Field;
Filter.Button = Button;
